import { Box, Text } from '@mantine/core'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OpportunityPickerModal } from '../LogVolunteeringActivityForm/OpportunityPickerModal/OpportunityPickerModal'
import classes from '../LogVolunteeringActivityForm/LogVolunteeringActivityForm.module.scss'

import { Button, ButtonText } from '@percent/lemonade'
import { OpportunityOptionResult } from '@percent/workplace-giving/common/components/OpportunityOptionResult/OpportunityOptionResult'
import type { OpportunitySelfParticipationDto } from '@percent/workplace-giving/api/opportunity/dto'

type OpportunityPickerSectionProps = Readonly<{
  selectedOpportunity?: OpportunitySelfParticipationDto['opportunity']
  proposedOpportunities?: OpportunitySelfParticipationDto[]
  onSelect: (opportunity: OpportunitySelfParticipationDto['opportunity']) => void
  allowOpportunityChange: boolean
  registeredOpportunitiesData?: OpportunitySelfParticipationDto[]
  registeredOpportunitiesQuery: string
  setRegisteredOpportunitiesQuery: (query: string) => void
  isFetchingRegisteredOpportunities: boolean
  isAwaitingFetchingRegisteredOpportunities: boolean
  fetchingRegisteredOpportunitiesError: boolean
}>

export function OpportunityPickerSection({
  selectedOpportunity,
  proposedOpportunities,
  onSelect,
  allowOpportunityChange,
  registeredOpportunitiesData,
  registeredOpportunitiesQuery,
  setRegisteredOpportunitiesQuery,
  isFetchingRegisteredOpportunities,
  isAwaitingFetchingRegisteredOpportunities,
  fetchingRegisteredOpportunitiesError
}: OpportunityPickerSectionProps) {
  const { t } = useTranslation()
  const [opportunityPickerModalOpen, setOpportunityPickerModalOpen] = useState(false)

  return (
    <Box>
      <Text className={classes.SectionTitle}>
        {t('workplace_giving.volunteering.activityForm.opportunityId.title')}
      </Text>

      {!selectedOpportunity ? (
        <Text className={classes.SectionDescription}>
          {t('workplace_giving.volunteering.activityForm.opportunityId.description')}
          <span>*</span>
        </Text>
      ) : null}

      <Box className={classes.ResultsWrapper}>
        {!selectedOpportunity &&
          proposedOpportunities?.slice(0, 3).map(({ opportunity }) => (
            <OpportunityOptionResult
              key={opportunity.id}
              id={opportunity.id}
              img={opportunity.image}
              name={opportunity.name}
              endDate={opportunity.endDate}
              actionBtn={
                <Button variant="secondary" size="small" shape="oval" onClick={() => onSelect(opportunity)}>
                  {t('workplace_giving.common.select')}
                </Button>
              }
            />
          ))}
        {selectedOpportunity && (
          <OpportunityOptionResult
            key={selectedOpportunity.id}
            id={selectedOpportunity.id}
            img={selectedOpportunity.image}
            name={selectedOpportunity.name}
            endDate={selectedOpportunity.endDate}
            actionBtn={
              allowOpportunityChange ? (
                <ButtonText onClick={() => setOpportunityPickerModalOpen(true)}>
                  {t('workplace_giving.common.change')}
                </ButtonText>
              ) : null
            }
          />
        )}
      </Box>

      {!selectedOpportunity && allowOpportunityChange ? (
        <ButtonText onClick={() => setOpportunityPickerModalOpen(true)}>
          {t('workplace_giving.common.seeAll')}
        </ButtonText>
      ) : null}

      {allowOpportunityChange ? (
        <OpportunityPickerModal
          open={opportunityPickerModalOpen}
          onClose={() => {
            setOpportunityPickerModalOpen(false)
            setRegisteredOpportunitiesQuery('')
          }}
          options={
            (!registeredOpportunitiesQuery && !fetchingRegisteredOpportunitiesError) ||
            isAwaitingFetchingRegisteredOpportunities
              ? proposedOpportunities
              : registeredOpportunitiesData
          }
          defaultOption={selectedOpportunity}
          onConfirm={(opportunity: OpportunitySelfParticipationDto['opportunity']) => onSelect(opportunity)}
          searchValue={registeredOpportunitiesQuery}
          isLoading={isFetchingRegisteredOpportunities}
          setSearchValue={query => setRegisteredOpportunitiesQuery(query)}
          hasErred={fetchingRegisteredOpportunitiesError}
        />
      ) : null}
    </Box>
  )
}
